import React, { useState, useEffect, useRef } from 'react';
import { Video, Menu, X, LogOut, User, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { authService, AUTH_CHANGED_EVENT } from '../services/auth.service.ts';
import UserAvatar from './UserAvatar.tsx';

function Navbar() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(() => window.scrollY > 50);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [user, setUser] = useState(authService.getUser());
  const userMenuRef = useRef(null);
  const scrollRafRef = useRef(0);
  const isScrolledRef = useRef(isScrolled);

  useEffect(() => {
    isScrolledRef.current = isScrolled;
  }, [isScrolled]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRafRef.current) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = 0;
        const next = window.scrollY > 50;
        // Sadece eşik değişince state güncelle — her karede re-render yok.
        if (next !== isScrolledRef.current) {
          isScrolledRef.current = next;
          setIsScrolled(next);
        }
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    const checkLoginStatus = () => {
      setUser(authService.getUser());
    };

    checkLoginStatus();
    window.addEventListener('storage', checkLoginStatus);
    window.addEventListener(AUTH_CHANGED_EVENT, checkLoginStatus);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', checkLoginStatus);
      window.removeEventListener(AUTH_CHANGED_EVENT, checkLoginStatus);
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!userMenuOpen) return;

    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    authService.logout();
    setUser(null);
    navigate('/');
  };

  const navbarClass = isScrolled
    ? 'bg-slate-900/95 shadow-lg border-b border-white/10'
    : 'bg-transparent border-b border-transparent';

  return (
    <nav className={`fixed w-full z-50 transition-[background-color,border-color,box-shadow] duration-200 ${navbarClass}`}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <Video className="w-8 h-8 text-purple-400" />
          <span className="text-2xl font-bold text-white">TBKMeet</span>
        </Link>

        <div className="hidden md:flex items-center space-x-8">
          {user && (
            <Link to="/dashboard" className="text-gray-300 hover:text-white transition">Panel</Link>
          )}
          <Link to="/features" className="text-gray-300 hover:text-white transition">Özellikler</Link>
          <Link to="/pricing" className="text-gray-300 hover:text-white transition">Fiyatlandırma</Link>
          <Link to="/about" className="text-gray-300 hover:text-white transition">Hakkımızda</Link>
          
          {user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((open) => !open)}
                className="flex items-center gap-2 text-gray-300 hover:text-white transition rounded-lg px-1.5 py-1 hover:bg-white/5"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                title="Hesap menüsü"
              >
                <UserAvatar
                  src={user.avatar}
                  name={user.name}
                  email={user.email}
                  provider={user.provider}
                  showProviderBadge
                  sizeClass="w-9 h-9"
                />
                <span className="text-sm font-medium max-w-[140px] truncate">{user.name}</span>
                <ChevronDown
                  className={`w-4 h-4 shrink-0 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {userMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-48 rounded-xl border border-white/10 bg-slate-900/95 shadow-xl overflow-hidden z-50"
                >
                  <Link
                    to="/profile"
                    role="menuitem"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-3 text-sm text-gray-200 hover:bg-white/10 hover:text-white transition"
                  >
                    <User className="w-4 h-4" />
                    <span>Profilim</span>
                  </Link>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 px-4 py-3 text-sm text-red-300 hover:bg-red-500/15 hover:text-red-200 transition border-t border-white/10"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Çıkış Yap</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className="text-gray-300 hover:text-white transition">Giriş Yap</Link>
              <Link to="/register" className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition transform hover:scale-105">Hemen Başla</Link>
            </>
          )}
        </div>

        <button
          className="md:hidden text-white p-2 min-h-[44px] min-w-[44px] flex items-center justify-center -mr-2 rounded-lg hover:bg-white/10 active:scale-95 transition"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-900/95 border-t border-white/10">
          <div className="px-6 py-4 space-y-4">
            {user && (
              <Link to="/dashboard" className="block text-gray-300 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>Panel</Link>
            )}
            <Link to="/features" className="block text-gray-300 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>Özellikler</Link>
            <Link to="/pricing" className="block text-gray-300 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>Fiyatlandırma</Link>
            <Link to="/about" className="block text-gray-300 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>Hakkımızda</Link>
            
            {user ? (
              <>
                <div className="flex items-center gap-3 text-white">
                  <UserAvatar
                    src={user.avatar}
                    name={user.name}
                    email={user.email}
                    provider={user.provider}
                    showProviderBadge
                    sizeClass="w-9 h-9"
                  />
                  <span className="text-sm font-medium truncate">{user.name}</span>
                </div>
                <Link
                  to="/profile"
                  className="flex items-center gap-2 text-gray-300 hover:text-white transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User className="w-5 h-5" />
                  <span>Profilim</span>
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-2 w-full px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition text-center justify-center"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Çıkış Yap</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="block text-gray-300 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>Giriş Yap</Link>
                <Link to="/register" className="block w-full px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition text-center" onClick={() => setMobileMenuOpen(false)}>Hemen Başla</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;